/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {ConnectionIssuesLinks} from '@app/components/ConnectionIssuesLinks';
import {FluxerIcon} from '@app/components/icons/FluxerIcon';
import {NativeDragRegion} from '@app/components/layout/NativeDragRegion';
import styles from '@app/components/layout/SplashScreen.module.css';
import {useSplashQuotes} from '@app/data/SplashQuotes';
import AccessibilityStore from '@app/stores/AccessibilityStore';
import DeveloperOptionsStore from '@app/stores/DeveloperOptionsStore';
import GatewayConnectionStore from '@app/stores/gateway/GatewayConnectionStore';
import InitializationStore from '@app/stores/InitializationStore';
import StatusPageStore from '@app/stores/StatusPageStore';
import {getReducedMotionProps} from '@app/utils/ReducedMotionAnimation';
import {ExternalUrls} from '@fluxer/constants/src/ExternalUrls';
import {Trans} from '@lingui/react/macro';
import {AnimatePresence, motion} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const SPLASH_SCREEN_DELAY = 10000;
const PROBLEMS_DELAY = 10000;
type SplashScreenMode = 'live' | 'outage';

export const SplashScreen = observer(() => {
	const shouldBypass = DeveloperOptionsStore.bypassSplashScreen;
	const connected = GatewayConnectionStore.isConnected;
	const isInitialized = InitializationStore.canNavigateToProtectedRoutes;
	const [showSplash, setShowSplash] = useState(true);

	useEffect(() => {
		if (connected && isInitialized) {
			setShowSplash(false);
			return;
		}

		const timer = setTimeout(() => setShowSplash(true), SPLASH_SCREEN_DELAY);
		return () => clearTimeout(timer);
	}, [connected, isInitialized]);

	if (shouldBypass) return null;
	return <AnimatePresence initial={false}>{showSplash && <SplashScreenContent mode="live" />}</AnimatePresence>;
});

export const OutageSplashScreen = observer(() => <SplashScreenContent mode="outage" />);

const SPLASH_MOTION = {
	initial: {opacity: 0},
	animate: {opacity: 1},
	exit: {opacity: 0},
	transition: {duration: 0.5},
};

const CONNECTION_ISSUES_MOTION = {
	initial: {opacity: 0},
	animate: {opacity: 1},
	exit: {opacity: 0},
	transition: {duration: 0.4},
};

const INCIDENT_CTA_MOTION = {
	initial: {opacity: 0},
	animate: {opacity: 1},
	exit: {opacity: 0},
	transition: {duration: 0.2},
};

interface SplashScreenContentProps {
	mode: SplashScreenMode;
}

function pickRandomQuote(quotes: ReadonlyArray<string>): string {
	return quotes[Math.floor(Math.random() * quotes.length)];
}

const SplashScreenContent = observer(({mode}: SplashScreenContentProps) => {
	const isOutageMode = mode === 'outage';
	const splashMotion = getReducedMotionProps(SPLASH_MOTION, AccessibilityStore.useReducedMotion);
	const connectionIssuesMotion = getReducedMotionProps(CONNECTION_ISSUES_MOTION, AccessibilityStore.useReducedMotion);
	const incidentCtaMotion = getReducedMotionProps(INCIDENT_CTA_MOTION, AccessibilityStore.useReducedMotion);
	const connected = isOutageMode ? false : GatewayConnectionStore.isConnected;
	const isInitialized = isOutageMode ? false : InitializationStore.canNavigateToProtectedRoutes;
	const isReady = connected && isInitialized;
	const splashQuotes = useSplashQuotes();
	const incident = StatusPageStore.incident;
	const incidentUrl = incident?.url ?? ExternalUrls.SERVICE_STATUS;

	const quote = useMemo(() => pickRandomQuote(splashQuotes), [splashQuotes]);
	const [showProblems, setShowProblems] = useState(isOutageMode);
	const problemsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const clearProblemsTimer = useCallback(() => {
		if (problemsTimerRef.current != null) {
			clearTimeout(problemsTimerRef.current);
			problemsTimerRef.current = null;
		}
	}, []);

	useEffect(() => {
		if (isOutageMode) {
			clearProblemsTimer();
			setShowProblems(true);
			void StatusPageStore.checkIncidents();
			return;
		}

		if (isReady) {
			clearProblemsTimer();
			setShowProblems(false);
			return;
		}

		problemsTimerRef.current = setTimeout(() => {
			setShowProblems(true);
			void StatusPageStore.checkIncidents();
		}, PROBLEMS_DELAY);

		return clearProblemsTimer;
	}, [clearProblemsTimer, isOutageMode, isReady]);

	const displayText = useMemo(() => {
		if (incident) {
			return incident.name;
		}
		return quote;
	}, [incident, quote]);

	const connectionStatusText = isOutageMode ? (
		<Trans>Connection lost</Trans>
	) : isReady ? (
		<Trans>Ready</Trans>
	) : (
		<Trans>Connecting</Trans>
	);

	if (isOutageMode) {
		return (
			<div className={styles.splashOverlay}>
				<NativeDragRegion className={styles.topDragRegion} />
				<div className={styles.splashContent}>
					<div className={styles.iconWrapper}>
						<div className={`${styles.iconPulse} ${styles.iconPulseStatic}`} />
						<FluxerIcon className={styles.icon} />
					</div>

					<div className={styles.quoteContainer}>
						{incident != null ? (
							<a href={incidentUrl} target="_blank" rel="noopener noreferrer" className={styles.quoteLink}>
								{displayText}
							</a>
						) : (
							<p className={styles.quote}>
								<Trans>Connection lost</Trans>
							</p>
						)}

						{incident != null && (
							<a href={incidentUrl} target="_blank" rel="noopener noreferrer" className={styles.incidentCta}>
								<Trans>View on status page</Trans>
							</a>
						)}
					</div>

					<p className={styles.connectionStatus}>{connectionStatusText}</p>
				</div>

				{showProblems && !isReady && (
					<div className={styles.connectionIssuesOverlay}>
						<ConnectionIssuesLinks incident={incident} />
					</div>
				)}
			</div>
		);
	}

	return (
		<motion.div {...splashMotion} className={styles.splashOverlay}>
			<NativeDragRegion className={styles.topDragRegion} />
			<div className={styles.splashContent}>
				<div className={styles.iconWrapper}>
					<div className={styles.iconPulse} />
					<FluxerIcon className={styles.icon} />
				</div>

				<div className={styles.quoteContainer}>
					{incident != null ? (
						<a href={incidentUrl} target="_blank" rel="noopener noreferrer" className={styles.quoteLink}>
							{displayText}
						</a>
					) : (
						<p className={styles.quote}>{displayText}</p>
					)}

					<AnimatePresence>
						{incident != null && (
							<motion.a
								{...incidentCtaMotion}
								href={incidentUrl}
								target="_blank"
								rel="noopener noreferrer"
								className={styles.incidentCta}
							>
								<Trans>View on status page</Trans>
							</motion.a>
						)}
					</AnimatePresence>
				</div>

				<p className={styles.connectionStatus}>{connectionStatusText}</p>
			</div>

			<AnimatePresence>
				{showProblems && !isReady && (
					<motion.div {...connectionIssuesMotion} className={styles.connectionIssuesOverlay}>
						<ConnectionIssuesLinks incident={incident} />
					</motion.div>
				)}
			</AnimatePresence>
		</motion.div>
	);
});
